import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../../app/providers/CompanyProvider';
import { ShoppingBagIcon, StoreIcon, GlobeIcon, PlusIcon, EyeIcon, EyeOffIcon, PackageIcon, DollarSignIcon, MoreHorizontalIcon, CreditCardIcon, TrashIcon, EditIcon, Share2Icon, SearchIcon, LinkIcon, CopyIcon, QrCodeIcon, ChevronRightIcon, XIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
// Shop type definition
export interface Shop {
  id: string;
  name: string;
  description: string;
  logo: string;
  color: string;
  isOpen: boolean;
  channels: ('pos' | 'ecommerce')[];
  productsCount: number;
  revenue: {
    total: number;
    currency: string;
    growth: number;
  };
  ordersCount: number;
  companyId: string;
}
export function Shops() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useCompany();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'open' | 'closed'>('all');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [shareModalShopId, setShareModalShopId] = useState<string | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const [shops, setShops] = useState<Shop[]>([{
    id: '1',
    name: 'Beach Shop',
    description: 'Beachwear and accessories for summer holidays',
    logo: '•',
    color: '#f8d7d7',
    isOpen: true,
    channels: ['pos', 'ecommerce'],
    productsCount: 48,
    revenue: {
      total: 12580,
      currency: getTenant().currency.code,
      growth: 15.3
    },
    ordersCount: 124,
    companyId: 'submerchant-kl-001'
  }, {
    id: '2',
    name: 'Island Shop',
    description: 'Local island products and souvenirs',
    logo: '+',
    color: '#e1f5fe',
    isOpen: true,
    channels: ['ecommerce'],
    productsCount: 32,
    revenue: {
      total: 8750,
      currency: getTenant().currency.code,
      growth: 8.7
    },
    ordersCount: 86,
    companyId: 'submerchant-kl-002'
  }, {
    id: '3',
    name: 'ABC',
    description: 'Convenience store with everyday essentials',
    logo: 'A',
    color: '#e8f5e9',
    isOpen: false,
    channels: ['pos'],
    productsCount: 156,
    revenue: {
      total: 24680,
      currency: getTenant().currency.code,
      growth: -2.4
    },
    ordersCount: 312,
    companyId: 'submerchant-kl-003'
  }]);
  // Filter shops by selected company
  const companyFilteredShops = selectedCompany.type === 'master' ? shops : shops.filter(s => s.companyId === selectedCompany.id);
  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleShopStatus = (shopId: string) => {
    setShops(shops.map(shop => shop.id === shopId ? {
      ...shop,
      isOpen: !shop.isOpen
    } : shop));
    setContextMenuOpenId(null);
  };
  const deleteShop = (shopId: string) => {
    if (confirm('Are you sure you want to delete this shop? This action cannot be undone.')) {
      setShops(shops.filter(shop => shop.id !== shopId));
    }
    setContextMenuOpenId(null);
  };
  const copyShopUrl = (shopId: string) => {
    const shopUrl = `https://yourwebsite.com/shop/${shopId}`;
    navigator.clipboard.writeText(shopUrl);
  };
  const filteredShops = companyFilteredShops.filter(shop => {
    const matchesSearch = searchQuery === '' || shop.name.toLowerCase().includes(searchQuery.toLowerCase()) || shop.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activeTab === 'open' && shop.isOpen || activeTab === 'closed' && !shop.isOpen;
    return matchesSearch && matchesTab;
  });
  const totalRevenue = companyFilteredShops.reduce((sum, s) => sum + s.revenue.total, 0);
  const totalProducts = companyFilteredShops.reduce((sum, s) => sum + s.productsCount, 0);
  const totalOrders = companyFilteredShops.reduce((sum, s) => sum + s.ordersCount, 0);
  const getStatusBadge = (isOpen: boolean) => {
    return isOpen ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800" data-id="element-11996">
        {t('components.getOrderStatusBadge.open')}
      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-800" data-id="element-11997">
        {t('pages.getStatusBadge.closed')}
      </span>;
  };
  return <div className="max-w-[1400px] mx-auto space-y-8" data-id="element-11998">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-11999">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12000">
          {t('pages.Shops.shops')}
        </h1>
        <div className="flex items-center gap-3" data-id="element-12001">
          <button onClick={() => navigate('/shop-details?new=true')} className="px-4 py-2 bg-[#FFC70A] hover:bg-[#D9A800] text-gray-900 text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12002">
            {t('pages.Shops.add_shop')}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-12003">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12004">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12005">
            <StoreIcon size={16} className="text-gray-500" data-id="element-12006" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12007">
              {t('pages.Shops.total_shops')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12008">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12009">
              {shops.length}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12010">
            {shops.filter(s => s.isOpen).length} {t('pages.Shops.open')}{' '}
            {shops.filter(s => !s.isOpen).length} {t('pages.Shops.closed')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12011">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12012">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-12013" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12014">
              {t('pages.Shops.total_revenue')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12015">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12016">
              {t('components.ShopDashboard.usd_2')}{' '}
              {totalRevenue.toLocaleString('en-US')}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12017">
            {t('pages.Orders.across')} {shops.length} {t('pages.Shops.shops_2')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12018">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12019">
            <PackageIcon size={16} className="text-gray-500" data-id="element-12020" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12021">
              {t('pages.Shops.total_products')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12022">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12023">
              {totalProducts}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12024">
            {t('pages.Shops.all_shops_combined')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-12025">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-12026">
            <ShoppingBagIcon size={16} className="text-gray-500" data-id="element-12027" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-12028">
              {t('pages.Orders.total_orders')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-12029">
            <div className="text-2xl font-bold text-gray-900" data-id="element-12030">
              {totalOrders}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-12031">
            {t('pages.Shops.all_shops_combined')}
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12032">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-12033">
          {/* Tabs */}
          <div className="flex space-x-6" role="tablist" aria-label="Shop status tabs" data-id="element-12034">
            {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'open',
            label: 'Open'
          }, {
            id: 'closed',
            label: 'Closed'
          }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls="shops-tabpanel" id={`shops-tab-${tab.id}`} onClick={() => setActiveTab(tab.id as 'all' | 'open' | 'closed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#FFC70A] text-[#B38A00]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-12035">
                {tab.label}
              </button>)}
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-64" data-id="element-12036">
            <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-12037" />

            <input type="search" aria-label={t('pages.Shops.attrs.search_shops')} placeholder={t('pages.Shops.attrs.search_shops')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFC70A]/20 focus:border-[#FFC70A] transition-colors" data-id="element-12038" />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-12039">
          <table className="w-full text-left border-collapse" data-id="element-12040">
            <thead data-id="element-12041">
              <tr className="border-b border-gray-100" data-id="element-12042">
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12043">
                  {t('pages.Orders.shop')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12044">
                  {t('pages.Shops.channels')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12045">
                  {t('components.CampaignList.status')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12046">
                  {t('pages.ShopDetails.products')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12047">
                  {t('components.ShopDashboard.revenue')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12048">
                  {t('pages.Orders.orders')}
                </th>
                <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-12049">
                  {t('pages.CampaignDetails.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-12050">
              {filteredShops.map(shop => <tr key={shop.id} onClick={() => navigate(`/shop-details?id=${shop.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-12051">
                  <td className="px-6 py-4" data-id="element-12052">
                    <div className="flex items-center gap-3" data-id="element-12053">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: shop.color
                  }} data-id="element-12054">
                        <span className="text-base font-bold text-red-600" data-id="element-12055">
                          {shop.logo}
                        </span>
                      </div>
                      <div data-id="element-12056">
                        <div className="text-sm font-medium text-gray-900 group-hover:text-[#16CD88] transition-colors" data-id="element-12057">
                          {shop.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5" data-id="element-12058">
                          {shop.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-12059">
                    <div className="flex items-center gap-1.5" data-id="element-12060">
                      {shop.channels.includes('pos') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-12061">
                          <CreditCardIcon size={11} className="mr-1" data-id="element-12062" />
                          {t('pages.ShopDetails.pos')}
                        </span>}
                      {shop.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-12063">
                          <GlobeIcon size={11} className="mr-1" data-id="element-12064" />
                          {t('pages.ShopDetails.ecommerce')}
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-12065">{getStatusBadge(shop.isOpen)}</td>
                  <td className="px-6 py-4" data-id="element-12066">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12067">
                      {shop.productsCount}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-12068">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12069">
                      {t('components.ShopDashboard.usd_2')}{' '}
                      {shop.revenue.total.toLocaleString('en-US')}
                    </div>
                    <div className={`text-xs mt-0.5 ${shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-12070">
                      {shop.revenue.growth >= 0 ? '+' : ''}
                      {shop.revenue.growth}%
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-12071">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12072">
                      {shop.ordersCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right relative" data-id="element-12073">
                    <div ref={contextMenuOpenId === shop.id ? contextMenuRef : null} data-id="element-12074">
                      <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" onClick={e => {
                    e.stopPropagation();
                    setContextMenuOpenId(contextMenuOpenId === shop.id ? null : shop.id);
                  }} aria-label="Shop actions" data-id="element-12075">
                        <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-12076" />
                      </button>

                      {contextMenuOpenId === shop.id && <>
                          <div className="fixed inset-0 z-10" onClick={e => {
                      e.stopPropagation();
                      setContextMenuOpenId(null);
                    }} data-id="element-12077" />

                          <div className="absolute right-4 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12078">
                            <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/shop-details?id=${shop.id}`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12079">
                              <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-12080" />

                              {t('pages.Orders.view_details')}
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        setContextMenuOpenId(null);
                        navigate(`/shop-details?id=${shop.id}&edit=true`);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12081">
                              <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-12082" />

                              {t('pages.ShopDetails.attrs.edit_shop')}
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        setShareModalShopId(shop.id);
                        setShowQRCode(false);
                        setContextMenuOpenId(null);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12083">
                              <Share2Icon size={16} className="mr-3 text-gray-400" data-id="element-12084" />

                              {t('pages.Shops.share_shop')}
                            </button>
                            <button onClick={e => {
                        e.stopPropagation();
                        toggleShopStatus(shop.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12085">
                              {shop.isOpen ? <>
                                  <EyeOffIcon size={16} className="mr-3 text-gray-400" data-id="element-12086" />

                                  {t('pages.Shops.close_shop')}
                                </> : <>
                                  <EyeIcon size={16} className="mr-3 text-gray-400" data-id="element-12087" />

                                  {t('pages.Shops.open_shop')}
                                </>}
                            </button>
                            <div className="border-t border-gray-100 my-1" data-id="element-12088" />
                            <button onClick={e => {
                        e.stopPropagation();
                        deleteShop(shop.id);
                      }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-12089">
                              <TrashIcon size={16} className="mr-3" data-id="element-12090" />
                              {t('pages.Shops.delete_shop')}
                            </button>
                          </div>
                        </>}
                    </div>
                  </td>
                </tr>)}
              {filteredShops.length === 0 && <tr data-id="element-12091">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-12092">
                    {t('pages.Shops.no_shops_found_matching_your_criteria')}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Share Modal */}
      {shareModalShopId && (() => {
      const shop = shops.find(s => s.id === shareModalShopId);
      if (!shop) return null;
      return <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-id="element-12093">
              <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200" data-id="element-12094">
                <div className="flex justify-between items-center mb-5" data-id="element-12095">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-12096">
                    {t('pages.Shops.share')} {shop.name}
                  </h3>
                  <button onClick={() => setShareModalShopId(null)} aria-label="Close" className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors" data-id="element-12097">
                    <XIcon size={18} aria-hidden="true" data-id="element-12098" />
                  </button>
                </div>

                {showQRCode ? <div className="text-center" data-id="element-12099">
                    <div className="bg-white p-4 rounded-lg inline-block mb-4 border border-gray-200" data-id="element-12100">
                      <div className="w-48 h-48 bg-gray-50 rounded flex items-center justify-center" data-id="element-12101">
                        <QrCodeIcon size={80} className="text-gray-300" data-id="element-12102" />
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-12103">
                      {t('pages.Shops.scan_this_qr_code_to_visit')} {shop.name}
                    </p>
                    <button onClick={() => setShowQRCode(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium transition-colors" data-id="element-12104">
                      {t('pages.Shops.back_to_share_options')}
                    </button>
                  </div> : <>
                    <div className="space-y-3 mb-5" data-id="element-12105">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-12106">
                        <div className="flex items-center" data-id="element-12107">
                          <LinkIcon size={16} className="text-gray-400 mr-2" data-id="element-12108" />
                          <span className="text-sm text-gray-600 truncate max-w-[220px]" data-id="element-12109">
                            https://yourwebsite.com/shop/{shareModalShopId}
                          </span>
                        </div>
                        <button onClick={() => copyShopUrl(shareModalShopId)} aria-label="Copy shop URL" className="p-1.5 text-[#B38A00] hover:bg-[#FFC70A]/10 rounded-md transition-colors" data-id="element-12110">
                          <CopyIcon size={16} aria-hidden="true" data-id="element-12111" />
                        </button>
                      </div>

                      <button onClick={() => setShowQRCode(true)} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors" data-id="element-12112">
                        <div className="flex items-center" data-id="element-12113">
                          <QrCodeIcon size={16} className="text-gray-400 mr-2" data-id="element-12114" />

                          <span className="text-sm text-gray-600" data-id="element-12115">
                            {t('pages.Shops.generate_qr_code')}
                          </span>
                        </div>
                        <ChevronRightIcon size={16} className="text-gray-400" data-id="element-12116" />
                      </button>
                    </div>

                    <div className="flex justify-end" data-id="element-12117">
                      <button onClick={() => setShareModalShopId(null)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 text-sm font-medium transition-colors" data-id="element-12118">
                        {t('pages.Shops.close')}
                      </button>
                    </div>
                  </>}
              </div>
            </div>;
    })()}
    </div>;
}